import React, { useCallback, useEffect, useRef, useState } from 'react';
import LabelEventGraphGeo from './LabelEventGraphGeo';
import {
  LABEL_TYPES,
  GeoIcon,
  COULEURS_PHASES,
  LabelHoverCard,
  LabelEditDialog,
  PhaseHoverCard,
  PhaseEditDialog,
} from './LabelComponents';
import Bouton from '../../../UI/Bouton/Bouton';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import {
  demoDonnees,
  demoTimestamps,
  demoY,
  demoConfiguration,
  demoDonneesGraphe,
} from './demoData';

// ── Constantes ────────────────────────────────────────────────────────────────

const HEADER_HEIGHT = 20;
const PADDING = 8;

const DATE_OPTIONS = demoTimestamps
  .map((ts, i) => ({ ts, y: demoY[i] }))
  .filter((_, i) => i % 2 === 0);

// ── Composant principal ───────────────────────────────────────────────────────

const LabelEventGraphGeoDemo = () => {
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const nextLabelId = useRef((demoConfiguration.labels || []).length);
  const prochainIdPhase = useRef((demoConfiguration.sections || []).length);
  const timerEtiquette = useRef(null);
  const timerPhase = useRef(null);

  const [dims, setDims] = useState({ width: undefined, height: undefined });
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [creatingLabel, setCreatingLabel] = useState(null);
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [creatingPhase, setCreatingPhase] = useState(null);

  const [labels, setLabels] = useState(() =>
    (demoConfiguration.labels || []).map((e, i) => {
      const labelType =
        e.labelType || (e.iconType === 'diamond' ? 'JALON' : 'IA');
      return {
        ...e,
        id: i,
        labelType,
        iconType: LABEL_TYPES[labelType].iconType,
      };
    })
  );

  const [phases, setPhases] = useState(() =>
    (demoConfiguration.sections || []).map((s, i) => ({ ...s, id: i }))
  );

  // ── Redimensionnement ────────────────────────────────────────────────────────

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDims({
          width: containerRef.current.offsetWidth - 28,
          height: window.innerHeight - HEADER_HEIGHT - PADDING,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // ── Fermer le menu au clic extérieur ─────────────────────────────────────────

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  // ── Rappels — étiquettes ─────────────────────────────────────────────────────

  const addLabel = useCallback((type = 'IA') => {
    const labelConfig = LABEL_TYPES[type];
    const midT = DATE_OPTIONS[Math.floor(DATE_OPTIONS.length / 2)].ts;
    setCreatingLabel({
      id: null,
      t: midT,
      iconType: labelConfig.iconType,
      labelType: type,
      label: '',
      note: '',
    });
  }, []);

  const handleSaveNewLabel = useCallback((draft) => {
    const id = nextLabelId.current++;
    setLabels((prev) => [...prev, { ...draft, id }]);
    setCreatingLabel(null);
  }, []);

  const handleCancelNewLabel = useCallback(() => setCreatingLabel(null), []);

  // Appelé par le bouton flottant au survol de la courbe signal
  const handleAddLabelFromSignal = useCallback(({ ts, yVal }) => {
    const labelConfig = LABEL_TYPES['JALON'];
    // Trouve l'horodatage le plus proche dans la liste des options de date
    const closest = DATE_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr.ts - ts) < Math.abs(prev.ts - ts) ? curr : prev
    );
    setCreatingLabel({
      id: null,
      t: closest.ts,
      iconType: labelConfig.iconType,
      labelType: 'JALON',
      label: '',
      note:
        yVal !== undefined ? `Valeur : ${Math.round(yVal * 10) / 10} dB` : '',
    });
  }, []);

  // ── Rappels — phases ─────────────────────────────────────────────────────────

  const ajouterPhase = useCallback(() => {
    const color =
      COULEURS_PHASES[prochainIdPhase.current % COULEURS_PHASES.length];
    setCreatingPhase({
      id: null,
      tStart: DATE_OPTIONS[0].ts,
      tEnd: DATE_OPTIONS[DATE_OPTIONS.length - 1].ts,
      label: '',
      color,
    });
  }, []);

  const handleSaveNewPhase = useCallback((draft) => {
    const id = prochainIdPhase.current++;
    setPhases((prev) => [...prev, { ...draft, id }]);
    setCreatingPhase(null);
  }, []);

  const handleCancelNewPhase = useCallback(() => setCreatingPhase(null), []);

  // ── Rappels survol / édition — étiquettes ────────────────────────────────────

  const handleLabelHover = useCallback(
    (t, x, y) => {
      const found = labels.find((l) => l.t === t);
      if (found) setHoveredLabel({ label: found, x, y });
    },
    [labels]
  );

  const handleLabelUnhover = useCallback(() => {
    timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 150);
  }, []);

  const handlePencilEdit = useCallback(() => {
    if (hoveredLabel) setEditingLabel({ ...hoveredLabel.label });
  }, [hoveredLabel]);

  const handleSaveLabel = useCallback((draft) => {
    setLabels((prev) =>
      prev.map((l) => (l.id === draft.id ? { ...draft } : l))
    );
    setEditingLabel(null);
    setHoveredLabel(null);
  }, []);

  const handleCancelEdit = useCallback(() => setEditingLabel(null), []);

  const handleRemoveLabel = useCallback(() => {
    if (hoveredLabel) {
      setLabels((prev) => prev.filter((l) => l.id !== hoveredLabel.label.id));
      setHoveredLabel(null);
    }
  }, [hoveredLabel]);

  // ── Rappels survol / édition — phases ────────────────────────────────────────

  const handlePhaseHover = useCallback(
    (phaseData, x, y) => {
      const found = phases.find(
        (s) => s.tStart === phaseData.tStart && s.tEnd === phaseData.tEnd
      );
      if (found) setHoveredPhase({ phase: found, x, y });
    },
    [phases]
  );

  const handlePhaseUnhover = useCallback(() => {
    timerPhase.current = setTimeout(() => setHoveredPhase(null), 150);
  }, []);

  const handlePhaseEdit = useCallback(() => {
    if (hoveredPhase) setEditingPhase({ ...hoveredPhase.phase });
  }, [hoveredPhase]);

  const handleSavePhase = useCallback((draft) => {
    setPhases((prev) =>
      prev.map((s) => (s.id === draft.id ? { ...draft } : s))
    );
    setEditingPhase(null);
    setHoveredPhase(null);
  }, []);

  const handleCancelPhaseEdit = useCallback(() => setEditingPhase(null), []);

  const handleDeletePhase = useCallback((draft) => {
    setPhases((prev) => prev.filter((s) => s.id !== draft.id));
    setEditingPhase(null);
    setHoveredPhase(null);
  }, []);

  const handleRemovePhase = useCallback(() => {
    if (hoveredPhase) {
      setPhases((prev) => prev.filter((s) => s.id !== hoveredPhase.phase.id));
      setHoveredPhase(null);
    }
  }, [hoveredPhase]);

  const handlePhaseClick = useCallback(
    (phaseData) => {
      const found = phases.find(
        (s) => s.tStart === phaseData.tStart && s.tEnd === phaseData.tEnd
      );
      if (found) setEditingPhase({ ...found });
    },
    [phases]
  );

  // ── Configuration du graphe ──────────────────────────────────────────────────

  const configuration = {
    ...demoConfiguration,
    title: 'LabelEventGraph — Labels IA et Jalons',
    labels: labels.map(({ id, labelType, ...rest }) => rest),
    sections: phases.map(({ id, ...rest }) => rest),
  };

  // ── Rendu ────────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="leg-demo-root">
      <div className="leg-demo-graph-panel">
        <div className="leg-demo-panel-title">
          <span>Démo — LabelEventGraph (Labels IA et Jalons)</span>
          <div ref={menuRef} className="leg-demo-menu-wrapper">
            <Bouton
              type={type_btn.succes}
              cliquer={() => setMenuOpen((o) => !o)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 14px 6px 10px',
                height: '34px',
                borderRadius: '6px',
                flexShrink: 0,
              }}
              title="Ajouter"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                className="leg-demo-btn-svg"
              >
                <path
                  fill="black"
                  d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm0 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm6.07-1.18a6.15 6.15 0 0 0 .05-.82c0-.28-.02-.55-.06-.82l1.77-1.38a.42.42 0 0 0 .1-.54l-1.68-2.9a.42.42 0 0 0-.52-.18l-2.08.84a6.1 6.1 0 0 0-1.41-.82L9.9.5A.42.42 0 0 0 9.5.1h-3a.42.42 0 0 0-.41.35l-.3 2.22a6.1 6.1 0 0 0-1.41.82L2.3 2.65a.42.42 0 0 0-.52.18L.1 5.74a.41.41 0 0 0 .1.54l1.77 1.38A6.3 6.3 0 0 0 1.9 8.5c0 .27.02.54.06.8L.19 10.7a.42.42 0 0 0-.1.54l1.68 2.9c.1.18.33.25.52.18l2.08-.83c.44.31.91.57 1.41.81l.3 2.22c.05.22.25.38.48.38h3.36c.23 0 .43-.16.48-.38l.3-2.22a6.1 6.1 0 0 0 1.41-.81l2.08.83c.2.07.42 0 .52-.18l1.68-2.9a.42.42 0 0 0-.1-.54l-1.77-1.4zm-1.01 2.17-.6-.24-.24-.1-1.73.7-.22 1.63H5.73l-.22-1.63-1.73-.7-.84.34-1.25-2.16 1.47-1.14-.06-.5A5.2 5.2 0 0 1 2.9 8.5c0-.22.01-.44.04-.66l.06-.5-1.47-1.14 1.25-2.16 2.05.82 1.73-.7L6.78 2.5h2.44l.22 1.66 1.73.7 2.05-.82 1.25 2.16-1.47 1.14.06.5c.03.22.04.44.04.66 0 .22-.01.44-.04.66l-.06.5 1.47 1.14-1.25 2.16z"
                />
              </svg>
              <span className="leg-demo-btn-label">Ajouter</span>
            </Bouton>
            {menuOpen && (
              <div className="leg-dropdown-menu">
                <button
                  className="leg-dropdown-item"
                  onClick={() => {
                    addLabel('JALON');
                    setMenuOpen(false);
                  }}
                >
                  <GeoIcon iconType="diamond" size={12} />
                  <span>Jalon</span>
                </button>
                <div className="leg-dropdown-divider" />
                <button
                  className="leg-dropdown-item"
                  onClick={() => {
                    ajouterPhase();
                    setMenuOpen(false);
                  }}
                >
                  <span>▬</span>
                  <span>Phase</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <LabelEventGraphGeo
          donnees={demoDonnees}
          configuration={configuration}
          donneesGraphe={demoDonneesGraphe(
            glossaire_type_de_graphe.label_event_graph_geo,
            'LabelEventGraph — Labels IA et Jalons'
          )}
          dimensionWidth={dims.width}
          dimensionHeight={dims.height ? dims.height - 40 : undefined}
          switchAfficherModifierIntervalle={() => {}}
          viewobj_json_content={{ displayModeBar: true }}
          onLabelHover={handleLabelHover}
          onLabelUnhover={handleLabelUnhover}
          onPhaseHover={handlePhaseHover}
          onPhaseUnhover={handlePhaseUnhover}
          onPhaseClick={handlePhaseClick}
          onAddLabel={handleAddLabelFromSignal}
        />
      </div>

      {/* Carte d'info label au survol */}
      {hoveredLabel && !editingLabel && (
        <LabelHoverCard
          labelData={hoveredLabel.label}
          x={hoveredLabel.x}
          y={hoveredLabel.y}
          onEdit={handlePencilEdit}
          onRemove={handleRemoveLabel}
          onMouseEnter={() => clearTimeout(timerEtiquette.current)}
          onMouseLeave={() => setHoveredLabel(null)}
        />
      )}

      {/* Dialog édition label */}
      {editingLabel && (
        <LabelEditDialog
          label={editingLabel}
          dateOptions={DATE_OPTIONS}
          onSave={handleSaveLabel}
          onCancel={handleCancelEdit}
        />
      )}

      {/* Dialog création label */}
      {creatingLabel && (
        <LabelEditDialog
          label={creatingLabel}
          dateOptions={DATE_OPTIONS}
          onSave={handleSaveNewLabel}
          onCancel={handleCancelNewLabel}
        />
      )}

      {/* Carte d'info phase au survol */}
      {hoveredPhase && !editingPhase && (
        <PhaseHoverCard
          phaseData={hoveredPhase.phase}
          x={hoveredPhase.x}
          y={hoveredPhase.y}
          onEdit={handlePhaseEdit}
          onRemove={handleRemovePhase}
          onMouseEnter={() => clearTimeout(timerPhase.current)}
          onMouseLeave={() => setHoveredPhase(null)}
        />
      )}

      {/* Dialog édition phase */}
      {editingPhase && (
        <PhaseEditDialog
          phase={editingPhase}
          onSave={handleSavePhase}
          onCancel={handleCancelPhaseEdit}
          onDelete={handleDeletePhase}
        />
      )}

      {/* Dialog création phase */}
      {creatingPhase && (
        <PhaseEditDialog
          phase={creatingPhase}
          onSave={handleSaveNewPhase}
          onCancel={handleCancelNewPhase}
        />
      )}
    </div>
  );
};

export default LabelEventGraphGeoDemo;
